import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Button from '../../../ui/Button';
import ToastService from '../../../services/ToastService';
import GameAPI, { createGameOperationId } from '../GameAPI';
import './TradingPost.css';

const read = (value, camelName, pascalName) => (
  value?.[camelName] ?? value?.[pascalName]
);
const EMPTY_INVENTORY = [];

function buildingLevel(building) {
  const configured = Number(
    read(building, 'currentLevel', 'CurrentLevel')
    ?? read(building, 'level', 'Level'),
  );
  if (Number.isFinite(configured)) return Math.max(0, Math.floor(configured));
  const status = String(read(building, 'status', 'Status') || '').toLowerCase();
  return status === 'completed' ? 1 : 0;
}

export function getTradingPostAccess(baseCamp) {
  const building = (baseCamp?.buildings || []).find(candidate => (
    String(candidate?.buildingKey || candidate?.BuildingKey || '').toLowerCase()
      === 'trading_post'
  ));
  const built = buildingLevel(building) > 0;
  return {
    built,
    canTransact: built && Boolean(baseCamp?.isInCamp ?? baseCamp?.IsInCamp),
    reason: !built
      ? 'Build the Trading Post in your Camp to list, buy, cancel, or claim.'
      : 'Enter your Camp and use the Trading Post to complete transactions.',
  };
}

export function normalizeMarketListing(listing) {
  if (!listing) return null;
  const listingId = read(listing, 'listingId', 'ListingId');
  if (!listingId) return null;
  return {
    listingId: String(listingId),
    sellerUserId: String(read(listing, 'sellerUserId', 'SellerUserId') || ''),
    itemId: String(read(listing, 'itemId', 'ItemId') || ''),
    itemInstanceId: String(
      read(listing, 'itemInstanceId', 'ItemInstanceId') || '',
    ),
    quantity: Math.max(1, Number(read(listing, 'quantity', 'Quantity')) || 1),
    price: Math.max(0, Number(read(listing, 'price', 'Price')) || 0),
    status: String(read(listing, 'status', 'Status') || 'active'),
    createdAtUtc: read(listing, 'createdAtUtc', 'CreatedAtUtc') || null,
    expiresAtUtc: read(listing, 'expiresAtUtc', 'ExpiresAtUtc') || null,
    isMine: Boolean(read(listing, 'isMine', 'IsMine')),
  };
}

export function normalizeMarketDelivery(delivery) {
  if (!delivery) return null;
  const deliveryId = read(delivery, 'deliveryId', 'DeliveryId');
  if (!deliveryId) return null;
  return {
    deliveryId: String(deliveryId),
    kind: String(read(delivery, 'kind', 'Kind') || '').toLowerCase(),
    itemId: String(read(delivery, 'itemId', 'ItemId') || ''),
    itemInstanceId: String(
      read(delivery, 'itemInstanceId', 'ItemInstanceId') || '',
    ),
    quantity: Math.max(0, Number(read(delivery, 'quantity', 'Quantity')) || 0),
    amountRemaining: Math.max(
      0,
      Number(read(delivery, 'amountRemaining', 'AmountRemaining')) || 0,
    ),
    status: String(read(delivery, 'status', 'Status') || 'pending'),
    createdAtUtc: read(delivery, 'createdAtUtc', 'CreatedAtUtc') || null,
  };
}

function itemDetails(entry) {
  return GameAPI.fetchItemAutoCached(entry?.itemId, entry?.itemInstanceId);
}

function itemName(entry) {
  return itemDetails(entry)?.name || entry?.itemId || 'Unknown item';
}

function isScrapMetal(entry) {
  const details = itemDetails(entry);
  return String(details?.name || '').trim().toLowerCase() === 'scrap metal'
    || String(entry?.itemId || '').trim().toLowerCase() === 'scrap_metal';
}

function slotKey(entry) {
  return [
    Number(entry?.x),
    Number(entry?.y),
    entry?.itemInstanceId || entry?.itemId,
  ].join(':');
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime())
    ? date.toLocaleString()
    : 'unknown';
}

export default function TradingPost({
  baseCamp,
  inventory = EMPTY_INVENTORY,
  onMutation,
}) {
  const [listings, setListings] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [price, setPrice] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [total, setTotal] = useState(0);
  const [, setItemRevision] = useState(0);
  const operationIdsRef = useRef(new Map());
  const access = getTradingPostAccess(baseCamp);

  const loadItemDetails = useCallback(async entries => {
    const identities = entries
      .filter(entry => entry?.itemId)
      .map(entry => ({
        itemId: entry.itemId,
        itemInstanceId: entry.itemInstanceId || '',
      }));
    if (!identities.length) return;
    try {
      await GameAPI.fetchItemsAuto(identities);
      setItemRevision(revision => revision + 1);
    } catch (loadError) {
      console.error('Market item details could not be loaded.', loadError);
    }
  }, []);

  const load = useCallback(async (searchQuery = '') => {
    setLoading(true);
    setError('');
    try {
      const listingPayload = await GameAPI.fetchMarketListings({
        q: String(searchQuery || '').trim(),
        page: 1,
        pageSize: 20,
      });
      const listingItems = (
        read(listingPayload, 'items', 'Items')
        ?? (Array.isArray(listingPayload) ? listingPayload : [])
      ).map(normalizeMarketListing).filter(Boolean);
      setListings(listingItems);
      setTotal(Math.max(
        listingItems.length,
        Number(read(listingPayload, 'total', 'Total')) || 0,
      ));

      let deliveryItems = [];
      if (access.built) {
        const deliveryPayload = await GameAPI.fetchMarketDeliveries();
        deliveryItems = (Array.isArray(deliveryPayload)
          ? deliveryPayload
          : read(deliveryPayload, 'items', 'Items') || [])
          .map(normalizeMarketDelivery)
          .filter(Boolean);
        setDeliveries(deliveryItems);
      } else {
        setDeliveries([]);
      }
      await loadItemDetails([
        ...listingItems,
        ...deliveryItems,
        ...inventory,
      ]);
    } catch (loadError) {
      setError(loadError?.message || 'The Trading Post could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [access.built, inventory, loadItemDetails]);

  useEffect(() => {
    load('');
  }, [load]);

  const eligibleInventory = inventory.filter(entry => (
    Number(entry?.quantity) > 0 && !isScrapMetal(entry)
  ));
  const resolvedSelectedSlot = eligibleInventory.some(entry => (
    slotKey(entry) === selectedSlot
  ))
    ? selectedSlot
    : eligibleInventory[0] ? slotKey(eligibleInventory[0]) : '';

  const ownListings = listings.filter(listing => listing.isMine).length;
  const operationFor = key => {
    const existing = operationIdsRef.current.get(key);
    if (existing) return existing;
    const operationId = createGameOperationId();
    operationIdsRef.current.set(key, operationId);
    return operationId;
  };

  const mutate = async (key, request, successMessage) => {
    if (busyKey) return;
    setBusyKey(key);
    setError('');
    setMessage('');
    try {
      const result = await request(operationFor(key));
      operationIdsRef.current.delete(key);
      onMutation?.(result);
      setMessage(successMessage);
      ToastService.push(successMessage, 'success');
      await load(query);
      return result;
    } catch (mutationError) {
      const status = Number(mutationError?.status);
      const code = String(
        mutationError?.details?.code
        ?? mutationError?.details?.Code
        ?? '',
      ).toUpperCase();
      if (
        Number.isInteger(status)
        && status >= 400
        && status < 500
        && ![408, 425, 429].includes(status)
        && !['OPERATION_IN_PROGRESS', 'OPERATION_PENDING'].includes(code)
      ) {
        operationIdsRef.current.delete(key);
      }
      setError(mutationError?.message || 'The market action could not be completed.');
      return null;
    } finally {
      setBusyKey('');
    }
  };

  const createListing = event => {
    event.preventDefault();
    const selected = eligibleInventory.find(entry => (
      slotKey(entry) === resolvedSelectedSlot
    ));
    const parsedPrice = Number(price);
    if (!access.canTransact || !selected) return;
    if (!Number.isInteger(parsedPrice) || parsedPrice < 1 || parsedPrice > 500) {
      setError('Choose a whole Scrap Metal price from 1 to 500.');
      return;
    }
    const key = `list:${resolvedSelectedSlot}:${parsedPrice}`;
    mutate(
      key,
      operationId => GameAPI.createMarketListing({
        operationId,
        itemId: selected.itemId,
        itemInstanceId: selected.itemInstanceId || '',
        sourceX: Number(selected.x),
        sourceY: Number(selected.y),
        price: parsedPrice,
      }),
      `Listed the full ${itemName(selected)} stack for ${parsedPrice} Scrap Metal.`,
    );
  };

  return (
    <section
      aria-busy={loading || Boolean(busyKey) || undefined}
      aria-label="Trading Post market"
      className="trading-post"
    >
      <div className="trading-post__toolbar">
        <Button
          disabled={loading || Boolean(busyKey)}
          onClick={() => load(query)}
          variant="ghost"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {!access.canTransact ? (
        <p className="trading-post__notice" role="status">
          Browsing is available here. {access.reason}
        </p>
      ) : null}
      {error ? <p className="trading-post__error" role="alert">{error}</p> : null}
      <p
        aria-live="polite"
        className="trading-post__message"
        role="status"
      >
        {message}
      </p>

      <form
        className="trading-post__search"
        onSubmit={event => {
          event.preventDefault();
          load(query);
        }}
        role="search"
      >
        <label>
          <span>Search listings</span>
          <input
            onChange={event => setQuery(event.target.value)}
            placeholder="Item name"
            type="search"
            value={query}
          />
        </label>
        <Button disabled={loading} type="submit" variant="secondary">
          Search
        </Button>
      </form>

      <section aria-labelledby="market-listings-title">
        <header className="trading-post__section-heading">
          <h3 id="market-listings-title">Listings</h3>
          <span>{total} found · {ownListings} yours</span>
        </header>
        {loading && listings.length === 0 ? (
          <p role="status">Loading market listings…</p>
        ) : listings.length === 0 ? (
          <p className="trading-post__empty">No matching listings.</p>
        ) : (
          <ul className="trading-post__cards">
            {listings.map(listing => (
              <li key={listing.listingId}>
                <div>
                  <strong>{itemName(listing)}</strong>
                  <span>
                    Full stack: {listing.quantity}
                    {' · '}
                    Expires {formatDate(listing.expiresAtUtc)}
                  </span>
                </div>
                <strong>{listing.price} Scrap Metal</strong>
                <Button
                  disabled={!access.canTransact || Boolean(busyKey)}
                  onClick={() => {
                    const verb = listing.isMine ? 'cancel' : 'buy';
                    const key = `${verb}:${listing.listingId}`;
                    mutate(
                      key,
                      operationId => (
                        listing.isMine
                          ? GameAPI.cancelMarketListing(
                            listing.listingId,
                            { operationId },
                          )
                          : GameAPI.buyMarketListing(
                            listing.listingId,
                            { operationId },
                          )
                      ),
                      listing.isMine
                        ? 'Listing cancelled. Its item is ready to claim.'
                        : 'Purchase completed. The item is ready to claim.',
                    );
                  }}
                  variant={listing.isMine ? 'secondary' : 'primary'}
                >
                  {busyKey.endsWith(listing.listingId)
                    ? `${listing.isMine ? 'Cancelling' : 'Buying'}…`
                    : listing.isMine ? 'Cancel listing' : 'Buy'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="market-create-title">
        <header className="trading-post__section-heading">
          <h3 id="market-create-title">Create listing</h3>
          <span>Maximum 10 active · 72 hours · 5% sale fee</span>
        </header>
        {access.canTransact ? (
          <form className="trading-post__listing-form" onSubmit={createListing}>
            <label>
              <span>Whole Pack stack</span>
              <select
                disabled={!eligibleInventory.length || ownListings >= 10}
                onChange={event => setSelectedSlot(event.target.value)}
                value={resolvedSelectedSlot}
              >
                {eligibleInventory.length ? eligibleInventory.map(entry => (
                  <option key={slotKey(entry)} value={slotKey(entry)}>
                    {itemName(entry)} × {entry.quantity}
                  </option>
                )) : (
                  <option value="">No tradeable Pack stack</option>
                )}
              </select>
            </label>
            <label>
              <span>Total price in Scrap Metal</span>
              <input
                inputMode="numeric"
                max="500"
                min="1"
                onChange={event => setPrice(event.target.value)}
                required
                type="number"
                value={price}
              />
            </label>
            <Button
              disabled={
                Boolean(busyKey)
                || !resolvedSelectedSlot
                || ownListings >= 10
              }
              type="submit"
              variant="primary"
            >
              {busyKey.startsWith('list:') ? 'Listing…' : 'List whole stack'}
            </Button>
          </form>
        ) : (
          <p className="trading-post__empty">{access.reason}</p>
        )}
      </section>

      {access.built ? (
        <section aria-labelledby="market-deliveries-title">
          <header className="trading-post__section-heading">
            <h3 id="market-deliveries-title">Deliveries</h3>
            <span>{deliveries.length} waiting</span>
          </header>
          {deliveries.length === 0 ? (
            <p className="trading-post__empty">No items or proceeds to claim.</p>
          ) : (
            <ul className="trading-post__cards">
              {deliveries.map(delivery => {
                const proceeds = delivery.kind === 'proceeds';
                const amount = proceeds
                  ? Math.min(20, delivery.amountRemaining)
                  : undefined;
                return (
                  <li key={delivery.deliveryId}>
                    <div>
                      <strong>
                        {proceeds
                          ? `${delivery.amountRemaining} Scrap Metal`
                          : `${itemName(delivery)} × ${delivery.quantity}`}
                      </strong>
                      <span>
                        {delivery.kind || 'delivery'}
                        {' · '}
                        {formatDate(delivery.createdAtUtc)}
                      </span>
                    </div>
                    <Button
                      disabled={!access.canTransact || Boolean(busyKey)}
                      onClick={() => mutate(
                        `claim:${delivery.deliveryId}:${amount || 'item'}`,
                        operationId => GameAPI.claimMarketDelivery(
                          delivery.deliveryId,
                          { operationId, amount },
                        ),
                        proceeds
                          ? `Claimed ${amount} Scrap Metal.`
                          : 'Delivery moved to your Pack.',
                      )}
                      variant="primary"
                    >
                      {busyKey.includes(delivery.deliveryId)
                        ? 'Claiming…'
                        : proceeds ? `Claim ${amount}` : 'Claim item'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : null}
    </section>
  );
}
